import { AvField, AvForm } from "availity-reactstrap-validation";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Alert, Button, Col, Row, Card, CustomInput } from "reactstrap";
import confirm from "reactstrap-confirm";
import { CurrentUserHasRole } from "../../api/auth";
import { getDepartments } from "../../api/department";
import { siteUserCreate, siteUserDelete, siteUserGet, siteUserUpdate, userUpdateSite, siteUserSendInvite } from "../../api/users";
import { features, isFeatureEnabled, roles } from "../../config";
import local from "../../localization/strings";

const SiteUserEdit = ({ site, userId, done }) => {
	const [data, setData] = useState(undefined);
	const [error, setError] = useState(undefined);
	const [departments, setDepartments] = useState([]);

	useEffect(() => {
		const LoadUser = async () => {
			var result = await siteUserGet(site.id, userId);

			if (result && !result.departmentId) {
				result.departmentId = "";
			}

			setData(result);
		};

		const LoadDepartments = async () => {
			setDepartments(await getDepartments());
		};

		if (userId) {
			LoadUser();
		} else {
			setData({});
		}

		if (isFeatureEnabled(features.Departments)) {
			LoadDepartments();
		}
	}, [site, userId]);

	const deleteUser = async () => {
		var result = await confirm({
			title: local.TF_Delete_User,
			message: local.TF_Delete_User_Are_You_Sure,
			confirmColor: "danger",
			confirmText: local.TF_Delete_User,
		});

		if (result) {
			await siteUserDelete(site.id, userId);
			toast.success(local.TF_User_successfully_deleted);
			done();
		}
	};

	async function save(_e, values) {
		if (userId) {
			const resultUpdate = await siteUserUpdate(site.id, values.email, values.displayName, values.role, site.displayName, values.newEmail, !!values.isSystemAccount);

			switch (resultUpdate) {
				case "LASTADMIN":
					setError(local.TS_LastAdmin);
					return;
				case "DuplicateUserName":
					setError(local.TS_EmailTaken);
					return;
				default:
					break;
			}

			if (values.departmentId !== data.departmentId) {
				await userUpdateSite(userId, null, values.departmentId);
			}

			setError(undefined);
			toast.success(local.TF_User_successfully_updated);
			done();
		} else {
			const resultCreate = await siteUserCreate(site.id, values.email, values.displayName, values.role, site.displayName, values.departmentId, values.password, false, !!values.isSystemAccount);

			switch (resultCreate) {
				case "DUP":
					setError(local.TS_Duplicate_User);
					return;
				case "UPGRADE":
					setError(local.TS_Max_Lite_Users);
					return;
				case "DuplicateUserName":
					setError(local.TS_LastAdmin);
					return;
				case "OK":
					setError(undefined);
					toast.success(local.TF_User_successfully_added);
					done();
					return;
				case "Existing":
					setError(undefined);
					toast.success(local.TF_User_successfully_added_Existing);
					done();
					return;
				default:
					setError(local.TF_PasswordRules);
					return;
			}
		}
	}

	const departmentValid = (value, ctx, _input, cb) => {
		let valid = true;

		switch (ctx.role) {
			case "DepartmentAdmin":
				if (value === "") {
					valid = local.TS_Required;
				}
				break;
			case "SiteAdmin":
				if (value !== "") {
					valid = local.TS_Invalid;
				}
				break;
			default:
				break;
		}
		cb(valid);
	};

	const inviteUser = async () => {
		const sent = await siteUserSendInvite(site.id, data.email);
		if (!sent) {
			toast.error(local.TF_SendInviteFailed);
			return;
		}
		toast.success(local.TF_SendInviteSuccessful);
		done();
	};

	return (
		<Card className="p-3">
			<Row>
				<Col className="text-left">
					<h5 className="mb-2">{userId ? local.TF_Editing_User : local.TF_Adding_User}</h5>
				</Col>
			</Row>
			{data && (
				<AvForm model={data} onValidSubmit={async (e, values) => await save(e, values)}>
					<AvField name="displayName" label={local.TS_Name} validate={{ required: { value: true, errorMessage: local.TS_Required } }} />
					<Row>
						<Col md={6}>
							<AvField type="select" name="role" label={local.TF_User_Type} validate={{ required: { value: true, errorMessage: local.TS_Required } }}>
								<option />
								<option value="SiteAdmin">{local.TS_Site_Administrator}</option>
								{isFeatureEnabled(features.Departments) && <option value="DepartmentAdmin">{local.TS_Department_Administrator}</option>}
								<option value="SecondaryUser">{local.TS_Secondary_User}</option>
							</AvField>
						</Col>
						{isFeatureEnabled(features.Departments) && (
							<Col md={6}>
								<AvField type="select" name="departmentId" label={local.TS_Department} validate={{ async: departmentValid }}>
									{/* If departments not in use, or user is site admin: */}
									<option value="">{local.TS_None}</option>
									{departments.map((item) => (
										<option key={item.id} value={item.id}>
											{item.name}
										</option>
									))}
								</AvField>
							</Col>
						)}
					</Row>
					<AvField name="email" label={local.TS_EmailAddress} disabled={userId !== undefined} validate={{ email: true, required: { value: userId === undefined, errorMessage: local.TS_Required } }} />
					{userId && <AvField name="newEmail" label={local.TS_NewEmailAddress} validate={{ email: true }} />}
					{!userId && <AvField name="password" label={local.TF_NewUserPassword} validate={{ required: { value: true, errorMessage: local.TS_Required } }} />}
					<AvField tag={CustomInput} type="checkbox" name="isSystemAccount" label="System account (IoT / automated; excluded from broadcast emails)" />
					{error && <Alert color="danger">{error}</Alert>}
					<Row>
						<Col>
							<Button color="primary" className="mt-2 float-left">
								{userId ? local.TS_Save_Changes : local.TF_Create_User}
							</Button>
							<Button color="secondary" className="mt-2 ml-2 float-left" onClick={done}>
								{local.TS_Cancel}
							</Button>
							{CurrentUserHasRole(roles.Distributor) && userId && (
								<Button color="primary" size="sm" className="mt-3 ml-2" onClick={inviteUser}>
									{local.TF_SendInvite}
								</Button>
							)}
						</Col>
						{CurrentUserHasRole(roles.SiteAdmin) && userId && (
							<Col>
								<Button color="danger" size="sm" className="mt-3 float-right" onClick={() => deleteUser()}>
									{local.TF_Delete_User}
								</Button>
							</Col>
						)}
					</Row>
				</AvForm>
			)}
		</Card>
	);
};

export default SiteUserEdit;
